import {
    useDynamicModalStore,
    type OpenModalOptions,
} from '@/store/modules/dynamicModals/store.ts';
import {
    isModalSlotOptions,
    ModalMode,
    type ComponentSlots,
    type ComponentWithEmits,
} from '@/store/modules/dynamicModals/types.ts';
import {
    injectDynamicModalOwner,
    type DynamicModalOwner,
} from '@/store/hooks/dynamicModalOwner.ts';

const guardCallbacks = <T>(callbacks: T | undefined, owner: DynamicModalOwner): T | undefined => {
    if (!callbacks || typeof callbacks !== 'object') {
        return callbacks;
    }

    return Object.fromEntries(
        Object.entries(callbacks).map(([name, value]) => [
            name,
            typeof value === 'function'
                ? (...args: unknown[]) => {
                      if (!owner.isActive()) {
                          return undefined;
                      }

                      return value(...args);
                  }
                : value,
        ])
    ) as T;
};

const guardSlotCallbacks = (
    slots: ComponentSlots | undefined,
    owner: DynamicModalOwner
): ComponentSlots | undefined => {
    if (!slots) {
        return undefined;
    }

    return Object.fromEntries(
        Object.entries(slots).map(([name, slot]) => {
            if (!isModalSlotOptions(slot)) {
                return [name, slot];
            }

            return [
                name,
                {
                    ...slot,
                    props: guardCallbacks(slot.props, owner),
                    emits: guardCallbacks(slot.emits, owner),
                },
            ];
        })
    );
};

const useDynamicModal = () => {
    const store = useDynamicModalStore();
    const owner = injectDynamicModalOwner();

    const openModal = <T extends ComponentWithEmits>(options: OpenModalOptions<T>) => {
        if (owner && !owner.isActive()) {
            return;
        }

        store.openModal({
            ...options,
            props: owner ? guardCallbacks(options.props, owner) : options.props,
            emits: owner ? guardCallbacks(options.emits, owner) : options.emits,
            slots: owner ? guardSlotCallbacks(options.slots, owner) : options.slots,
            owner,
        });
    };

    const openDrawer = <T extends ComponentWithEmits>(
        options: Omit<OpenModalOptions<T>, 'mode'>
    ) => {
        openModal({
            ...options,
            mode: ModalMode.Drawer,
        });
    };

    return {
        closeModalByName: store.closeModalByName,
        closeModalsByName: store.closeModalsByName,
        openDrawer,
        openModal,
    };
};

export default useDynamicModal;
